import { useMemo } from "react";
import useSWR, { cache, mutate } from "swr";

import { LightSpace, ProductType } from ".";
import { MALFORMED_RESPONSE_FINGERPRINT } from "../core/api_failure";
import { useAuth0 } from "../core/auth0";
import { WeekDay } from "../lib/datetime";
import { fetchJsonWithRetry, toRequestUrl } from "../lib/fetch_with_retry";
import { useI18n } from "../lib/i18n/i18n_provider";
import { getDurationInHours } from "../offering/time_utils";
import { UnavailabilityReason } from "../offering/recurrence";
import { Booking } from "./booking";
import { ImageData, ImageVersion } from "./image";
import { Location } from "./location";
import { Order, OrderableType, OrderItem, PaymentMethod } from "./orders";
import {
  CatalogBrand,
  GeoMarket,
  LocationBrand,
  MembershipDetail,
  OfferingItemsGroupBy,
  Partner,
  PartnerOffering,
  PartnerOfferingLocation,
  Products,
  LightPartnerOffering,
  SortOrder,
} from "./partner_offering";
import { toBrand } from "@/offering_v2/brand";
import {
  Amenity,
  BookingHour,
  DynamicPriceRate,
  Pricing,
  PricingType,
  Space,
  SpaceBookingPolicy,
  SpaceLayout,
  toPriceRanges,
} from "./space";

// A date of a recurring booking that submit could not claim, as the 422
// body's `dates` names it — the same reason vocabulary the picker shows.
export interface UnavailableDate {
  date: string;
  reason?: UnavailabilityReason;
}

export class APIError extends Error {
  /**
   * Whether this error is worth reporting rather than just rendering. A server
   * that answers with a shaped error chose to say so, and the UI it drives is
   * the report; a subclass raised because nothing usable arrived overrides
   * this. Reading the flag keeps that judgement on the error instead of asking
   * every handler to remember which subtypes are the exception.
   */
  readonly reportable: boolean = false;

  /**
   * The name every report types this exception by. Written as a literal here
   * and restated by each subclass, rather than read off the constructor: a
   * minifier renames the class, so `this.constructor.name` yields whatever the
   * build happened to rename it to. Reporting types an exception by this field
   * and groups on the result, so one recurring fault is filed under a fresh
   * name — and as a fresh, empty-looking group — every time the bundle is
   * rebuilt, and no single group ever shows the fault's real rate.
   */
  name = "APIError";

  code?: string;
  status?: number;
  dates?: UnavailableDate[];
  /**
   * Which rule refused an ordinary (non-recurring) booking. The API sends it
   * alongside `booking_unavailable` precisely so a booker refused by a lead
   * time is not told to go and find a different room; `dates` carries the same
   * answer per date when the booking repeats.
   */
  reason?: UnavailabilityReason;
  /** The request behind the failure, where the raiser knows it. */
  url?: string;

  constructor(
    message: string,
    code?: string,
    status?: number,
    dates?: UnavailableDate[],
    reason?: UnavailabilityReason
  ) {
    super(message);
    this.code = code;
    this.status = status;
    this.dates = dates;
    this.reason = reason;
  }
}

export class UnauthorizedAPIError extends APIError {
  name = "UnauthorizedAPIError";
}

/**
 * A response whose body could not be read as JSON, after the read was already
 * retried — typically a proxy or CDN error page standing in for the API, or a
 * payload truncated in transit.
 *
 * It extends `APIError` so the status it arrived under still drives the
 * handling every other API failure gets, but it stays a distinct type because
 * the two are not the same event: a well-formed error is a server answering,
 * while this is nobody's answer and remains worth reporting.
 */
class MalformedResponseError extends APIError {
  readonly reportable = true;

  /**
   * Every occurrence is the same fault wherever it was raised, so it names the
   * group itself rather than leaving reporting to infer one from where it was
   * thrown — which would file each call site that reads JSON separately.
   */
  readonly reportingGroup = MALFORMED_RESPONSE_FINGERPRINT;

  name = "MalformedResponseError";

  constructor(status: number, url: string) {
    // The request that caused it rides along as a field, where reporting can
    // read it without splitting the group per URL.
    super("Malformed response body", undefined, status);
    this.url = url;
  }
}

interface ErrorBody {
  message: string;
  code?: string;
}

/**
 * Reads the conventional error fields off a parsed body.
 *
 * The body is whatever the server sent, so neither field is guaranteed. An
 * absent message resolves to the empty string every caller would otherwise
 * have to substitute, which keeps `undefined` from reaching something that
 * renders or reports it as though it were the reason.
 */
export function toErrorBody(body: unknown): ErrorBody {
  if (typeof body !== "object" || body === null) {
    return { message: "" };
  }

  const message = "message" in body ? body.message : undefined;
  const code = "code" in body ? body.code : undefined;

  return {
    message: typeof message === "string" ? message : "",
    code: typeof code === "string" ? code : undefined,
  };
}

/**
 * Fetches JSON, replaying a request whose payload arrives unreadable, and
 * raises the still-unreadable case as an error carrying the status.
 *
 * Every JS engine words a parse failure differently, so letting the raw error
 * escape splits one fault into as many reports as there are browsers, none of
 * them naming the request that caused it.
 */
export async function requestJson(
  input: RequestInfo,
  init?: RequestInit
): Promise<{ status: number; body: unknown }> {
  const result = await fetchJsonWithRetry(input, init);

  if (!result.parsed) {
    throw new MalformedResponseError(result.status, toRequestUrl(input));
  }

  return { status: result.status, body: result.body };
}

/**
 * API error codes returned by the backend.
 */
export const API_ERROR_CODES = {
  /** User has an active membership that blocks checkout */
  ACTIVE_MEMBERSHIP_BLOCKED: "active_membership_blocked",
  /**
   * Partner has banned this booker: by email address, by email domain, or by
   * the company name and billing address they typed.
   */
  BLACKLIST: "blacklist",
  /**
   * The window the booker asked for cannot be booked, and the API could read
   * the room to say so. It carries a `reason` naming which rule the engine hit
   * — booked, closed, blocked out, under the minimum, inside the lead time,
   * elapsed, or past the cutoff — and, on a recurring booking, `dates` naming
   * each lost date.
   *
   * A verdict about the room, unlike `UNABLE_TO_VERIFY_AVAILABILITY` below.
   */
  BOOKING_UNAVAILABLE: "booking_unavailable",
  /**
   * The API could not read the room at all — an integration outage, a rate
   * limit, a read that never answered — so nothing is known about whether the
   * window was free. A booking refused under this may have been refused over a
   * failure on our side.
   */
  UNABLE_TO_VERIFY_AVAILABILITY: "unable_to_verify_availability",
  /**
   * The partner's booking policy requires the number of guests and the quote
   * named none. Answered at quote time, so the checkout box maps it onto the
   * guest count picker rather than onto the pay button.
   */
  NUMBER_OF_GUESTS_REQUIRED: "number_of_guests_required",
  /**
   * The space is reserved for same-day day-pass holders and the checkout email
   * holds no qualifying pass. Answered at quote time (once the email is typed)
   * and again at submit; the backend message tells the booker what to buy
   * first, so it renders inline above the confirm button, never as a toast.
   */
  DAY_PASS_REQUIRED: "day_pass_required",
} as const;

export function toAmenity(data: any): Amenity {
  return {
    id: data.id,
    name: data.name,
    iconURL: data.icon_url,
  };
}

function toPricing(data: any): Pricing {
  return {
    id: data.id,
    type:
      data.pricing_type === "hourly" ? PricingType.Hourly : PricingType.Daily,
    price: Number(data.price),
  };
}

function toSpaceLayout(data: any): SpaceLayout {
  return {
    id: data.id,
    layoutID: data.layout_id,
    name: data.name,
    imageURL: data.image_url,
    capacity: data.capacity,
    description: data.description,
    spaceID: data.space_id,
    priceRanges: toPriceRanges(data.price_ranges),
    pricings: data.pricings.map(toPricing),
  };
}

function toBookingHour(data: any): BookingHour {
  let dayOfWeek = WeekDay.Monday;

  switch (data.day_of_week) {
    case "Monday":
      dayOfWeek = WeekDay.Monday;
      break;
    case "Tuesday":
      dayOfWeek = WeekDay.Tuesday;
      break;
    case "Wednesday":
      dayOfWeek = WeekDay.Wednesday;
      break;
    case "Thursday":
      dayOfWeek = WeekDay.Thursday;
      break;
    case "Friday":
      dayOfWeek = WeekDay.Friday;
      break;
    case "Saturday":
      dayOfWeek = WeekDay.Saturday;
      break;
    case "Sunday":
      dayOfWeek = WeekDay.Sunday;
      break;
    default:
      break;
  }
  return {
    dayOfWeek,
    openAt: data.open_at,
    closedAt: data.closed_at,
  };
}

export function toSpaceBookingPolicy(data: any): SpaceBookingPolicy {
  return {
    askForTax: data.ask_for_tax_number,
    requireTaxId: data.require_tax_id,
    requireCompany: data.require_company ?? false,
    requireBillingState: data.require_billing_state ?? false,
    minDuration: data.min_duration_hours,
    minDurationByDayOfWeek: data.min_duration_hours_by_day_of_week,
    disclaimer: data.disclaimer,
    checkoutWarning: data.checkout_warning,
    marketingConsent: data.marketing_consent,
    marketingConsentCheckedByDefault: data.marketing_consent_checked_by_default,
    terms: data.terms,
    askForBookingReason: data.ask_for_booking_reason,
    askForSpecialInstructions: data.ask_for_special_instructions,
    requestToBook: data.request_to_book,
    recurringEnabled: data.recurring_enabled ?? false,
    recurringEligible: data.recurring_eligible ?? false,
    askForNumberOfGuests: data.ask_for_number_of_guests,
    requireNumberOfGuests: data.require_number_of_guests ?? false,
    cancellationWarning: data.cancellation_warning,
    cancellationFeeTiers: data.cancellation_fee_tiers?.map((tier: any) => ({
      daysBefore: tier.days_before,
      hoursBefore: tier.hours_before,
      time1DayBefore: tier.time_1_day_before,
      refundPercentage: Number(tier.refund_percentage),
    })),
  };
}

export function toSpace(data: any): Space {
  return {
    id: data.id,
    spaceType: data.space_type,
    name: data.name,
    description: data.description,
    inventoryCapacity: data.inventory_capacity,
    sellableInventoryCount: data.sellable_inventory_count,
    currencyCode: data.currency || data.currency_code || null,
    defaultLayoutID: data.default_space_layout_id,
    images: data.images.map(toImage),
    layouts: data.space_layouts.map(toSpaceLayout),
    city: data.city,
    status: data.active ? "active" : "inactive",
    country: data.country,
    streetAddress: data.street_address,
    squareFootage: data.square_footage,
    attributes: {
      floor: data.floor,
      withWindow: data.with_window,
    },
    priceRanges: toPriceRanges(data.price_ranges),
    pricings: data.pricings.map(toPricing),
    bookingHours: data.booking_hours.map(toBookingHour),
    dailyAccessBookingHours: data.daily_access_booking_hours
      ? data.daily_access_booking_hours.map(toBookingHour)
      : undefined,
    bookingHour: data.booking_hour
      ? toBookingHour(data.booking_hour)
      : undefined,
    bookingPolicy: toSpaceBookingPolicy(data.booking_policy),
    requestToBook: data.booking_policy?.request_to_book ?? false,
    locationID: data.location_id,
    locationTimeZone: data.location_time_zone,
    productType: toProductType(data.product_type),
    partnerID: data.partner_id,
    dynamicPriceRates: data.dynamic_price_rates.map(toDynamicPriceRate),
    smartPricingEnabled: data.smart_pricing_enabled,
    // Older API responses omit this; default to supported so the gate falls back
    // to prior "enabled only" behavior instead of hiding real smart-priced rooms.
    smartPricingSupported: data.smart_pricing_supported ?? true,
  };
}

export function toLightSpace(data: any): LightSpace {
  return {
    id: data.id,
    name: data.name,
    currencyCode: data.currency || data.currency_code || null,
    images: data.images.map(toImage),
    layouts: data.space_layouts.map(toSpaceLayout),
    streetAddress: data.street_address,
    priceRanges: toPriceRanges(data.price_ranges),
    pricings: data.pricings.map(toPricing),
    dynamicPriceRates: data.dynamic_price_rates.map(toDynamicPriceRate),
    bookingHour: data.booking_hour
      ? toBookingHour(data.booking_hour)
      : undefined,
    requestToBook: data.request_to_book ?? false,
    bookable: data.bookable === true,
  };
}

function toDynamicPriceRate(data: any): DynamicPriceRate | undefined {
  if (data) {
    return {
      bookingCriteria: data.booking_criteria.map((bC: any) => ({
        minBookingUnit: bC.min_booking_unit
          ? Number(bC.min_booking_unit)
          : undefined,
        bookingType: bC.booking_type,
      })),
      dynamicPriceFormula: {
        adjustmentType: data.dynamic_price_formula.adjustment_type,
        adjustmentValue: Math.abs(
          Number(data.dynamic_price_formula.adjustment_value)
        ),
      },
    };
  }
}

function toProductType(data: any): ProductType | null {
  if (!data) {
    return null;
  }

  return {
    id: data.id,
    name: data.name,
    type: data.type,
    invites: data.invites,
    selectQuantity: data.select_quantity,
  };
}

function toImage(data: any): ImageVersion {
  return {
    original: toImageData(data.original),
    thumb: toImageData(data.thumb),
    small: toImageData(data.small),
    medium: toImageData(data.medium),
    large: toImageData(data.large),
    xlarge: toImageData(data.xlarge),
  };
}

function toImageData(data: any): ImageData {
  return {
    url: data.url,
    width: data.width,
    height: data.height,
  };
}

export function toLocation(data: any): Location {
  return {
    id: data.id,
    maxCapacity: data.max_capacity,
    name: data.name,
    logo: data.logo ? toImageData(data.logo) : null,
    streetAddress: data.street_address,
    city: data.city,
    timezone: data.time_zone,
    state: data.state,
    stateName: data.state_name,
    wlHomeLink: data.wl_home_link,
    postalCode: data.postal_code,
    currencyCode: data.currency || null,
    country: data.country,
    images: data.images?.map(toImage),
    direction: data.direction,
    longitude: Number(data.longitude),
    latitude: Number(data.latitude),
    pricingInclusion: data.pricing_inclusion,
    locationDisplay: data.location_display,
    customLocationDisplay: data.custom_location_display || undefined,
    googleAction: data.google_action
      ? toGoogleAction(data.google_action)
      : undefined,
  };
}

function toGoogleAction(data: any): Location["googleAction"] {
  return {
    entityId: data.entity_id,
    name: data.name,
    active: data.active,
  };
}

interface PurchaseStatusResult {
  has_portal_account: boolean;
  purchased_membership_portal: boolean;
  purchased_membership_wo_portal: boolean;
  purchased_multi_pass: boolean;
}

export interface PurchaseStatus {
  hasPortalAccount: boolean;
  purchasedMembershipPortal: boolean;
  purchasedMembershipWoPortal: boolean;
  purchasedMultiPass: boolean;
}

export interface PartnerOfferingParams {
  city?: string;
  state?: string;
  locationID?: string;
  locationIDs?: string[];
  minCapacity?: number;
  maxCapacity?: number;
  startDate?: string;
  endDate?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  layoutId?: string;
  productCategory?: string;
  productTypeID?: string;
}

export function useGetPartnerOffering(
  offeringID: string,
  params?: PartnerOfferingParams
): PartnerOffering {
  let searchParams = new URLSearchParams();
  let qs = "";
  const { locale } = useI18n();

  if (params) {
    if (params.state) {
      searchParams.set("state", params.state);
    }
    if (params.city) {
      searchParams.set("city", params.city);
    }
    if (params.locationID) {
      searchParams.set("location_id", params.locationID);
    }
    if (params.locationIDs) {
      searchParams.set("location_ids", String(params.locationIDs));
    }
    if (params.minCapacity) {
      searchParams.set("min_capacity", String(params.minCapacity));
    }
    if (params.maxCapacity) {
      searchParams.set("max_capacity", String(params.maxCapacity));
    }
    if (params.startDate) {
      searchParams.set("start_date", params.startDate);
    }
    if (params.endDate) {
      searchParams.set("end_date", params.endDate);
    }
    if (params.date) {
      searchParams.set("date", params.date);
    }
    if (params.startTime) {
      searchParams.set("start_time", params.startTime);
      if (params.endTime) {
        searchParams.set("end_time", params.endTime);
      }
    }
    if (params.layoutId) {
      searchParams.set("layout_id", params.layoutId);
    }
    if (params.productCategory) {
      searchParams.set("product_category", params.productCategory);
    }
    if (params.productTypeID) {
      searchParams.set("product_type_id", params.productTypeID);
    }

    qs = `?${searchParams.toString()}`;
  }

  if (qs) {
    qs += `&locale=${locale.languageTag}`;
  } else {
    qs += `?locale=${locale.languageTag}`;
  }

  // Revalidate on focus so the offering payload (FAQ, pricing, availability) refreshes
  // when the tab regains focus — a saved dashboard edit then surfaces on the live page
  // without a hard reload, matching the freshness amenities get from SWR's default.
  // Background revalidation reuses cached data, so it won't re-suspend or flash the loader.
  const response = useSWR(`/api/v2/partner_offerings/${offeringID}${qs}`, {
    suspense: true,
    revalidateOnFocus: true,
  });

  return useMemo(() => toPartnerOffering(response.data), [response.data]);
}

export function useGetLightPartnerOffering(
  offeringID: string
): LightPartnerOffering {
  let searchParams = new URLSearchParams();
  let qs: string;
  const { locale } = useI18n();
  searchParams.set("light_response", "true");
  searchParams.set("locale", locale.languageTag);
  qs = `?${searchParams.toString()}`;

  const response = useSWR(`/api/v2/partner_offerings/${offeringID}${qs}`, {
    suspense: true,
    revalidateOnFocus: false,
  });

  return useMemo(() => toLightPartnerOffering(response.data), [response.data]);
}

export function useProductsByProductTypeQuery(
  productTypeID: string,
  partnerID: string
) {
  const response = useSWR(
    `/api/v2/partners/${partnerID}/product_type/${productTypeID}/products`
  );

  const data = useMemo(
    () => (response.data ? toProducts(response.data) : null),
    [response.data]
  );

  return {
    data,
    loading: partnerID && productTypeID && !response.data,
  };
}

function toPartnerOffering(data: any): PartnerOffering {
  return {
    id: data.id,
    name: data.name,
    offeringTitle: data.offering_title,
    template: data.template,
    description: data.description,
    addOns: data.add_ons.map(toMultiPassAddOn),
    partner: toPartner(data.partner),
    templateName: data.template_name,
    offeringLocations: data.offering_locations.map(toOfferingLocation),
    unlistedOfferingLocations: (data.unlisted_offering_locations ?? []).map(
      toOfferingLocation
    ),
    promoCodeBoxContent: data.promo_code_box_content,
    generalInfo: data.general_info,
    showDiscountLabel: data.show_discount_label,
    faqs: data.faqs,
    slug: data.slug,
    cancellationPolicy: data.cancellation_policy,
    usePartnerOfferingImagesAcrossLocations:
      data.use_partner_offering_images_across_locations,
    allowSelectableStartDate: data.allow_selectable_start_date,
    singleDaySelect: data.single_day_select,
    quantitySelect: data.quantity_select,
    showSinglePass: data.show_single_pass,
    redirectedSlug: data.redirected_slug,
    offeringItemsSort:
      data.offering_items_sort === "capacity_desc"
        ? SortOrder.HighestToLowest
        : SortOrder.LowestToHighest,
    offeringItemsGroupBy:
      data.offering_items_group_by === "location"
        ? OfferingItemsGroupBy.Location
        : OfferingItemsGroupBy.City,
    hideHourlyGalleryWhenFilter: data.hide_hourly_gallery_when_filter,
    // Off unless the payload says on, so one that predates the field — a cached
    // copy, say — never swaps a partner's memberships section for rooms.
    showRelatedMeetingRoomCarousel:
      data.show_related_meeting_room_carousel === true,
    // The room route and its checkout read this payload, not the light one, and both
    // identify a place by space. Without it neither resolves a brand at all.
    locationBrands: toLocationBrands(data.location_brands),
    catalogBrands: toCatalogBrands(data.branded_catalogs),
    brand: toBrand(data.brand),
  };
}

// The partner's branded catalogs, from the same payload. An offering page reached with
// `?fromCatalog=` resolves its brand through this rather than a second request.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toCatalogBrands(data: any): CatalogBrand[] {
  return (data ?? []).flatMap((entry: any) => {
    const brand = toBrand(entry.brand);
    return brand ? [{ slug: entry.slug ?? "", brand }] : [];
  });
}

// Entries whose brand does not survive mapping are dropped rather than carried with an
// empty identity: `useResolvedBrand` treats any entry as an answer, so a half-mapped one
// would resolve a brand with no colours and no logo, which renders worse than none.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toLocationBrands(data: any): LocationBrand[] {
  return (data ?? []).flatMap((entry: any) => {
    const brand = toBrand(entry.brand);
    return brand
      ? [{ id: entry.id, spaceIds: entry.space_ids ?? [], brand }]
      : [];
  });
}

function toLightPartnerOffering(data: any): LightPartnerOffering {
  return {
    id: data.id,
    name: data.name,
    offeringTitle: data.offering_title,
    template: data.template,
    description: data.description,
    partner: toPartner(data.partner),
    promoCodeBoxContent: data.promo_code_box_content,
    generalInfo: data.general_info,
    showDiscountLabel: data.show_discount_label,
    faqs: data.faqs,
    slug: data.slug,
    redirectedSlug: data.redirected_slug,
    hasActiveListingSpaces: data.has_active_listing_spaces,
    locationBrands: toLocationBrands(data.location_brands),
    catalogBrands: toCatalogBrands(data.branded_catalogs),
    brand: toBrand(data.brand),
  };
}

function toPartner(data: any): Partner {
  return {
    id: data.id,
    name: data.name,
    partnerSlug: data.partner_slug,
    logoUrl: data.logo_url,
    homepageUrl: data.homepage_url,
    internalName: data.internal_name,
    facebook_url: data.facebook_url,
    instagram_url: data.instagram_url,
    twitter_url: data.twitter_url,
    linkedin_url: data.linkedin_url,
    tiktok_url: data.tiktok_url,
  };
}

function toProducts(data: any): Products {
  return {
    partner: data.partner,
    spaceLocations: data.space_locations.map(toOfferingLocation),
    addOns: data.add_ons ? data.add_ons.map(toMultiPassAddOn) : [],
    templateName: data.template_name,
  };
}

function toMultiPassAddOn(data: any) {
  return {
    id: data.id,
    addOnID: data.id,
    name: data.name,
    description: data.description,
    numberOfEntries: data.number_of_entries,
    daysToExpire: data.days_to_expire,
    sortOrder: data.sort_order,
    credits: data.meta_data?.credits,
    productCategory: data.product_category,
    productTypeName: data.product_type_name,
  };
}

function toOfferingLocation(data: any): PartnerOfferingLocation {
  return {
    id: data.id,
    spaceID: data.space_id,
    integrationProductID: data.integration_product_id,
    locationDisplay: data.location_display,
    customLocationDisplay: data.custom_location_display || undefined,
    location: {
      ...(data?.location || {}),
      city: data.location?.city,
      stateName: data.location?.state_name,
      currencyCode: data.location?.currency,
      streetAddress: data.location?.street_address,
    },
    offeringItemType: data.offering_item_type,
    membership: data.membership
      ? toOfferingMembership(data.membership)
      : undefined,
    promoCodeBoxContent: data.promo_code_box_content,
    featuredPromoDescription: data.featured_promo_description,
    generalInfo: data.general_info,
    faqs: data.faqs,
    sortOrder: data.sort_order ?? undefined,
    spaceMaxCapacity: data.space_max_capacity,
    spaceLayoutCount: data.space_layout_count,
    productCategory: data.product_category,
    productType: data.product_type
      ? { id: data.product_type.id, name: data.product_type.name }
      : undefined,
    showSinglePass: data.show_single_pass,
  };
}

export function isPartnerLocation(
  locatable?: Location | GeoMarket
): locatable is Location {
  if (!locatable) return false;
  return (locatable as Location).id !== undefined;
}

const toLocatableLocation = (locatable: any): Location => ({
  ...(locatable || {}),
  city: locatable?.city,
  currencyCode: locatable?.currency,
  streetAddress: locatable?.street_address,
  latitude: Number(locatable.latitude),
  longitude: Number(locatable.longitude),
});

function toOfferingMembership(data: any): MembershipDetail {
  let locatable: Location | GeoMarket | undefined;

  if (isPartnerLocation(data.locatable)) {
    locatable = toLocatableLocation(data.locatable);
  } else if (!data.locatable) {
    locatable = undefined;
  } else {
    locatable = {
      id: data.locatable.id,
      name: data.locatable.name,
      description: data.locatable.description!,
      locations: data.locatable.locations.map(toLocatableLocation),
    };
  }

  return {
    currency: data.currency,
    description: data.description,
    id: data.id,
    locatable,
    locatableType: data.locatable_type,
    monthlyPrice: Number(data.monthly_price),
    name: data.name,
    partnerId: data.partner_id,
    stripeProductId: data.stripe_product_id,
    timeZone: data.time_zone,
    yearlyPrice: Number(data.yearly_price),
    firstPaymentCycles: data.first_payment_cycles,
    images: data.images,
    inventory: data.inventory,
    productCategory: data.product_category,
    productTypeName: data.product_type_name,
    recurring: data.recurring,
    requestToSubscribe: data.request_to_subscribe ?? false,
    requestToSubscribeConfirmationText:
      data.request_to_subscribe_confirmation_text ?? undefined,
    requestToSubscribeCTALabel:
      data.request_to_subscribe_cta_label ?? undefined,
  };
}

export function useGetSpace(spaceID: string): Space {
  const response = useSWR(`/api/v2/spaces/${spaceID}`, { suspense: true });

  return toSpace(response.data);
}

export function useMaybeGetSpace(spaceID: string | null): Space | null {
  const response = useSWR(
    () => (spaceID ? `/api/v2/spaces/${spaceID}` : null),
    {
      suspense: true,
    }
  );
  if (!response.data) {
    return null;
  }
  return toSpace(response.data);
}

export type AvailabilityType =
  | "buffered"
  | "booked"
  | "available"
  | "blocked_out"
  | "not_enough_min_booking_hours"
  | "past_available"
  | "lead_time";

export interface AvailabilitySlot {
  from: string;
  to: string;
  available: boolean;
  type: AvailabilityType;
}

interface PhysicalSpaceCalendar {
  physicalSpaceID: string;
  availabilities: AvailabilitySlot[];
}

interface SpaceCalendar {
  physicalSpaces: PhysicalSpaceCalendar[];
  open: boolean;
  date: string;
  spaceID: string;
}

interface SpaceCalendarParam {
  date: string;
}

function toSpaceCalendarSearchParams(
  spaceID: string,
  param: SpaceCalendarParam
) {
  const searchParams = new URLSearchParams();

  searchParams.set("space_id", spaceID);
  searchParams.set("date", param.date);

  return `?${searchParams.toString()}`;
}

function toSpaceCalendar(data: any): SpaceCalendar {
  return {
    date: data.date,
    open: data.open,
    spaceID: data.space_id,
    physicalSpaces: data.data.map(
      (data: any): PhysicalSpaceCalendar => ({
        physicalSpaceID: data.physical_space_id,
        availabilities: data.data,
      })
    ),
  };
}

export function useGetSpaceCalendar(
  spaceID: string,
  param: SpaceCalendarParam
): SpaceCalendar {
  const response = useSWR(
    `/api/bookings/space_calendar${toSpaceCalendarSearchParams(
      spaceID,
      param
    )}`,
    { suspense: true }
  );

  return toSpaceCalendar(response.data);
}
interface PhysicalSpace {
  id: string;
  floor: string;
  room: string;
}

function toPhysicalSpace(data: any): PhysicalSpace {
  return {
    id: data.id,
    floor: data.floor,
    room: data.name,
  };
}

export function useGetSpacePhysicalSpaces(spaceID: string): PhysicalSpace[] {
  const response = useSWR(
    () => (spaceID ? `/api/spaces/${spaceID}/physical_spaces` : null),
    {
      suspense: true,
    }
  );

  if (!response.data) {
    return [];
  }

  return response.data.map(toPhysicalSpace);
}

interface BookingsParam {
  startDate: string;
  endDate: string;
}

interface SearchBookingsParams extends BookingsParam {
  spaceID: string;
}

function toBookingSearchParams(param: SearchBookingsParams) {
  const searchParams = new URLSearchParams();

  searchParams.set("start_date", param.startDate);
  searchParams.set("end_date", param.endDate);
  searchParams.set("space_id", param.spaceID);

  return `?${searchParams.toString()}`;
}

export function useGetSpaceBookings(
  spaceID: string,
  param: BookingsParam
): Booking[] {
  const response = useSWR(
    param
      ? `/api/v2/bookings${toBookingSearchParams({ ...param, spaceID })}`
      : null,
    { suspense: true }
  );

  return response.data.map(toBooking);
}

function toBooking(data: any): Booking {
  const duration =
    data.end_time && data.start_time
      ? String(getDurationInHours(data.end_time, data.start_time))
      : data.duration;

  return {
    id: data.id,
    attendees: data.attendees,
    spaceID: data.space_id,
    spaceType: data.space_type,
    bookingType: data.booking_type,
    layoutID: data.space_layout_id,
    date: data.date,
    time: data.time || data.start_time,
    duration: duration,
    startDate: data.start_date,
    endDate: data.end_date,
    room: data.name,
    days: data.days || data.duration,
    floor: data.floor,
    orderID: data.order_id,
    originalOrderId: data.original_order_id,
    orderConfirmationID: data.order_confirmation_id,
    orderCreatedAt: new Date(data.order_created_at * 1000), // unix timestamp in SECONDS
    orderName: data.order_fullname,
    orderEmail: data.order_email,
    physicalSpaceID: data.physical_space_id,
    cancellationAllowed: data.cancellation_allowed,
    cancellationAllowedBefore: data.cancellation_allowed_before
      ? // unix timestamp in SECONDS
        new Date(data.cancellation_allowed_before * 1000)
      : null,
  };
}

function toOrderItem(data: any): OrderItem {
  const orderableType: OrderableType.Booking = data.orderable_type || data.type;

  return {
    orderableType,
    unitPrice: data.unit_price,
    subTotal: data.sub_total,
    id: data.id,
    details: toBooking(data.details),
  };
}

function toPaymentMethod(data: any): PaymentMethod | null {
  if (!data || !data.card) {
    return null;
  }

  return {
    card: {
      last4: data.card.last4,
      network: data.card.network,
      networkLogoUrl: data.card.network_logo_url,
    },
  };
}

function toOrder(data: any): Order {
  return {
    id: data.id,
    confirmationID: data.confirmation_id,
    status: data.status,
    currencyCode: data.currency,
    stripeClientSecret: data.stripe_client_secret,
    totalPrice: data.total_price,
    additionalNotes: data.additional_notes,
    guestName: data.fullname,
    guestEmail: data.email,
    guestCompanyName: data.company_name,
    guestPhoneNumber: data.phone,
    orderItems: data.order_items.map(toOrderItem),
    paymentMethodDetails: toPaymentMethod(data.payment_method_details),
    createdAt: new Date(data.created_at * 1000),
  };
}

export interface BlockOutDates {
  id: string;
  spaceID: string;
  startDate: string;
  endDate: string | null;
  reason: string;
}

function toBlockOutDates(data: any): BlockOutDates {
  return {
    id: data.id,
    spaceID: data.space_id,
    startDate: data.start_date,
    endDate: data.end_date,
    reason: data.reason,
  };
}

interface DateRangeParams {
  startDate: string;
  endDate: string;
}

function toSpaceDateRange(
  spaceID: string,
  param: DateRangeParams & { capacity?: number; physicalSpaceID?: string }
) {
  const searchParams = new URLSearchParams();

  searchParams.set("space_id", spaceID);

  searchParams.set("start_date", param.startDate);
  searchParams.set("end_date", param.endDate);

  if (param.capacity) {
    searchParams.set("capacity", `${param.capacity}`);
  }
  if (param.physicalSpaceID) {
    searchParams.set("physical_space_id", `${param.physicalSpaceID}`);
  }

  return `?${searchParams.toString()}`;
}

export function useGetSpaceBlockOutDates(
  spaceID: string,
  param: {
    startDate: string;
    endDate: string;
  }
): BlockOutDates[] {
  const response = useSWR(
    () =>
      spaceID
        ? `/api/partners/bookings/block_out_dates${toSpaceDateRange(
            spaceID,
            param
          )}`
        : null,
    {
      suspense: true,
    }
  );

  if (!response.data) {
    return [];
  }

  return response.data.map(toBlockOutDates);
}

export async function invalidateSpaceCache(spaceID: string) {
  const keys = cache.keys();
  // Delete any cache that includes the spaceID in its key
  const spaceKeys = keys.filter((k) => k.includes(spaceID));

  for (let k of spaceKeys) {
    await mutate(k);
  }
}

export function useInvalidateSpaceCache() {
  return async (spaceID: string): Promise<void> => {
    await invalidateSpaceCache(spaceID);

    return;
  };
}

export function useCancelBooking() {
  const post = usePost();

  return async (bookingID: string): Promise<void> => {
    await post(`/api/v2/bookings/${bookingID}/cancel`);

    return;
  };
}

export function useGetPartnerOrder(orderID: string): Order {
  const response = useSWR(`/api/v2/partners/orders/${orderID}`, {
    suspense: true,
  });

  return toOrder(response.data);
}

export function useGetLocationsByPartnerOfferingSlug(
  slug: string
): Array<Location> {
  let searchParams = new URLSearchParams();
  let qs: string;
  const { locale } = useI18n();
  searchParams.set("locale", locale.languageTag);
  qs = `?${searchParams.toString()}`;

  const response = useSWR(`/api/v2/partner_offerings/${slug}/locations${qs}`, {
    suspense: true,
  });

  return response.data.map(toLocation);
}

interface CheckPurchaseStatusInputs {
  email: string;
  partnerId: string;
}

export function useCheckPurchaseStatus(inputs: CheckPurchaseStatusInputs) {
  const { email, partnerId } = inputs;
  const response = useSWR<PurchaseStatusResult>(
    `/api/v2/partners/${partnerId}/purchase_status/?email=${encodeURIComponent(
      email
    )}`,
    {
      suspense: true,
    }
  );

  const {
    has_portal_account: hasPortalAccount = false,
    purchased_membership_portal: purchasedMembershipPortal = false,
    purchased_membership_wo_portal: purchasedMembershipWoPortal = false,
    purchased_multi_pass: purchasedMultiPass = false,
  } = response.data || {};

  return {
    hasPortalAccount,
    purchasedMembershipPortal,
    purchasedMembershipWoPortal,
    purchasedMultiPass,
  };
}

function usePost() {
  const { token } = useAuth0();

  return async (url: string, data?: any) => {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    const body = await response.json();

    if (response.status >= 200 && response.status < 300) {
      return body;
    }

    throw new APIError(
      body.message,
      body.code,
      response.status,
      body.dates,
      body.reason
    );
  };
}
